import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { chicagoTodayIso } from '../utils/weekDates.js';

/** The API's anchor for "today"; see `chicagoTodayIso`. */
const CHICAGO = 'America/Chicago';

/**
 * The current Chicago calendar date, re-rendered when it actually changes.
 *
 * `chicagoTodayIso()` in a query key looks like it rotates the cache at midnight, and
 * does not: it is only evaluated when React renders. Any keystroke, filter change or
 * window refocus re-evaluates it, so the gap is narrow — but a tab left focused, open
 * and untouched across midnight keeps yesterday's `daysPastDue`, urgency rails and
 * counts, while the comment above that key promises the opposite. A guarantee the code
 * states is a guarantee the code should keep.
 *
 * A timer to the next boundary rather than a poll: one wake-up per day instead of one
 * per minute, and it costs nothing while the tab sits idle.
 */
export function useChicagoToday(): string {
  const [today, setToday] = useState(chicagoTodayIso);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const schedule = (): void => {
      timer = setTimeout(() => {
        // Re-read rather than increment: the answer must come from the same function
        // the key uses, or the two can disagree across a DST shift.
        setToday(chicagoTodayIso());
        schedule();
      }, msUntilNextChicagoMidnight(new Date()));
    };

    schedule();
    return () => clearTimeout(timer);
  }, []);

  return today;
}

/**
 * How far `date` is from UTC in the app's zone, in milliseconds, at that instant.
 *
 * The client twin of the server's `zoneOffsetMs` in `repositories/deadlines.ts`, and
 * kept deliberately identical: both answer "what does the wall clock read there", and
 * two different answers to that would put the browser's idea of a day boundary out of
 * step with the server's idea of a deadline.
 */
function zoneOffsetMs(date: Date): number {
  const parts = OFFSET_FMT.formatToParts(date);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asIfUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    // `hour` comes back as 24 at midnight under hour12: false in some ICU versions.
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return asIfUtc - date.getTime();
}

const OFFSET_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: CHICAGO,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * The instant the next Chicago midnight occurs at.
 *
 * Resolved as a wall-clock time in the zone, not by adding 24 hours to the last one.
 * A day there is 24 hours on 363 days a year and 23 or 25 on the other two, so a fixed
 * remainder is wrong exactly when a boundary most needs care: from midnight on the
 * spring-forward day it fired an hour LATE (01:00 the next day, with the date already
 * changed and the counts stale for that hour), and on the fall-back day an hour EARLY,
 * costing a wasted wake-up. An earlier revision documented that as an acceptable
 * over-wait; it is not, when the whole reason this timer exists is to keep a boundary
 * the code promises to keep.
 *
 * Two passes for the same reason the server's `zonedWallClockToInstant` uses two: the
 * offset near the guess may not be the offset at the answer. Midnight itself is never
 * a nonexistent wall time in the US — transitions land at 02:00 — so there is no third
 * case to handle here.
 *
 * Exported for tests, which is why the instant is a parameter.
 */
export function nextChicagoMidnight(from: Date): Date {
  const [y, m, d] = chicagoTodayIso(from).split('-').map(Number) as [number, number, number];
  // Midnight of the NEXT calendar day, as a naive wall-clock reading. Date.UTC
  // normalises a day past the end of the month.
  const naiveMidnight = Date.UTC(y, m - 1, d + 1, 0, 0, 0);
  const firstPass = new Date(naiveMidnight - zoneOffsetMs(new Date(naiveMidnight)));
  return new Date(naiveMidnight - zoneOffsetMs(firstPass));
}

/**
 * Milliseconds from `from` until the next Chicago midnight.
 *
 * Never zero or negative: a timer that fires immediately would spin.
 */
export function msUntilNextChicagoMidnight(from: Date): number {
  return Math.max(1_000, nextChicagoMidnight(from).getTime() - from.getTime());
}

/**
 * Invalidate a query family when a specific instant passes.
 *
 * The companion to {@link useChicagoToday}, for a boundary the calendar does not know
 * about. Round 4 made the server's badge rule instant-accurate — a not-yet-submitted
 * week stops being excluded the moment its `dueAt` passes, not at the following
 * midnight — and a browser with no reason to re-render would have shown the old count
 * until midnight anyway, throwing that precision away for the one viewer most likely to
 * be watching: someone sitting on the approvals page at 17:00 on a Monday.
 *
 * Invalidation rather than a key change, because the data is stale at the same key;
 * nothing about the request has changed. A single timer to the earliest future instant,
 * rescheduled whenever that instant changes, so an idle tab wakes once per deadline
 * rather than polling.
 *
 * `null` disables it, which is what an empty or fully-past list of deadlines means.
 */
export function useInvalidateAt(at: string | null, queryKey: readonly unknown[]): void {
  const queryClient = useQueryClient();
  // Serialised so the effect re-runs when the KEY's contents change rather than on every
  // render that rebuilds the array literal.
  const keyId = JSON.stringify(queryKey);

  useEffect(() => {
    if (at === null) return;
    const delay = Date.parse(at) - Date.now();
    if (Number.isNaN(delay)) return;
    if (delay <= 0) {
      // Already past by the time this ran — the response crossed the deadline while it
      // was in flight, so the count it carries is stale on arrival. Returning here left
      // it that way until midnight. There is no loop: the server only ever nominates a
      // deadline still in its own future, so the refetch comes back with a later instant
      // or none, and the effect does not re-run on an unchanged `at`.
      void queryClient.invalidateQueries({ queryKey: JSON.parse(keyId) as unknown[] });
      return;
    }
    // A timeout longer than a signed 32-bit millisecond count fires immediately in every
    // browser engine, which would make a distant deadline a spin rather than a wait.
    if (delay > 2_147_483_647) return;

    const timer = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: JSON.parse(keyId) as unknown[] });
    }, delay);
    return () => clearTimeout(timer);
  }, [at, keyId, queryClient]);
}
